/**
 * Tutorial System - Central Export
 *
 * Exports all tutorial definitions and provides utility functions
 * for accessing and managing tutorials.
 */

import { TutorialDefinition, TutorialCategory } from './tutorialDefinitions';
import { generalTutorials } from './generalTutorials';
import { battleTutorials } from './battleTutorials';
import { progressionTutorials } from './progressionTutorials';
import { explorationTutorials } from './explorationTutorials';
import { craftingTutorials } from './craftingTutorials';
import { TOWN_SYSTEM_TUTORIAL, ACHIEVEMENTS_TUTORIAL } from './townTutorials';

// Export type definitions
export * from './tutorialDefinitions';

// Combine all tutorials
export const ALL_TUTORIALS: TutorialDefinition[] = [
  ...generalTutorials,
  ...battleTutorials,
  ...progressionTutorials,
  ...explorationTutorials,
  ...craftingTutorials,
  TOWN_SYSTEM_TUTORIAL,
  ACHIEVEMENTS_TUTORIAL
];

/**
 * Get a tutorial by its key
 */
export const getTutorialByKey = (key: string): TutorialDefinition | undefined => {
  return ALL_TUTORIALS.find(t => t.key === key);
};

/**
 * Get all tutorials for a specific category
 */
export const getTutorialsByCategory = (category: TutorialCategory): TutorialDefinition[] => {
  return ALL_TUTORIALS.filter(t => t.category === category);
};

/**
 * Get all tutorial keys (useful for stats endpoint)
 */
export const getAllTutorialKeys = (): string[] => {
  return ALL_TUTORIALS.map(t => t.key);
};

/**
 * Get tutorials sorted by priority
 */
export const getTutorialsByPriority = (): TutorialDefinition[] => {
  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  return [...ALL_TUTORIALS].sort((a, b) => {
    const orderA = priorityOrder[a.priority];
    const orderB = priorityOrder[b.priority];
    return orderA - orderB;
  });
};

/**
 * Check if a tutorial has prerequisites that are not completed
 */
export const hasUnmetPrerequisites = (
  tutorialKey: string,
  completedTutorialKeys: string[]
): boolean => {
  const tutorial = getTutorialByKey(tutorialKey);
  if (!tutorial || !tutorial.prerequisites) return false;

  return tutorial.prerequisites.some(prereq => !completedTutorialKeys.includes(prereq));
};
